import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause } from 'lucide-react';

const BARS = 40;
const SPEEDS = [1, 1.5, 2];

export const fmtDur = (s) => {
  if (s == null || !isFinite(s)) return '0:00';
  const v = Math.max(0, Math.round(s));
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, '0')}`;
};

// stable pseudo-waveform when the recording came without one
const fakeBars = (seed) => {
  let x = (seed || 1) * 9301 + 49297;
  return Array.from({ length: BARS }, () => { x = (x * 9301 + 49297) % 233280; return 0.25 + (x / 233280) * 0.7; });
};

const resample = (arr) => {
  if (!arr || !arr.length) return null;
  return Array.from({ length: BARS }, (_, i) => {
    const a = Math.floor((i * arr.length) / BARS), b = Math.max(a + 1, Math.floor(((i + 1) * arr.length) / BARS));
    const slice = arr.slice(a, b);
    return Math.max(0.12, Math.max(...slice));
  });
};

/** Voice message: play/pause, tappable waveform that fills as it plays, time and speed. Only one plays at a time. */
export default function VoicePlayer({ id, src, duration, waveform, mine }) {
  const audio = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(duration || 0);
  const [speed, setSpeed] = useState(1);
  const bars = useMemo(() => resample(waveform) || fakeBars(id), [waveform, id]);

  useEffect(() => {
    const stopOthers = (e) => { if (e.detail !== id && audio.current && !audio.current.paused) audio.current.pause(); };
    window.addEventListener('chat-voice-play', stopOthers);
    return () => window.removeEventListener('chat-voice-play', stopOthers);
  }, [id]);

  useEffect(() => {
    let raf;
    const tick = () => { if (audio.current) setPos(audio.current.currentTime); raf = requestAnimationFrame(tick); };
    if (playing) raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  const toggle = () => {
    const a = audio.current;
    if (!a) return;
    if (a.paused) {
      window.dispatchEvent(new CustomEvent('chat-voice-play', { detail: id }));
      a.playbackRate = speed;
      a.play().catch(() => setPlaying(false));
    } else a.pause();
  };

  const seek = (e) => {
    const a = audio.current;
    if (!a || !dur) return;
    const r = e.currentTarget.getBoundingClientRect();
    const rtl = getComputedStyle(e.currentTarget).direction === 'rtl';
    let frac = (e.clientX - r.left) / r.width;
    if (rtl) frac = 1 - frac;
    a.currentTime = Math.min(dur, Math.max(0, frac * dur));
    setPos(a.currentTime);
  };

  const cycleSpeed = () => {
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
    setSpeed(next);
    if (audio.current) audio.current.playbackRate = next;
  };

  const progress = dur ? pos / dur : 0;
  return (
    <div className={`voice ${mine ? 'mine' : ''} ${playing ? 'is-playing' : ''}`}>
      <button type="button" className="voice-play" onClick={toggle} aria-label={playing ? 'pause' : 'play'}>
        {playing ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" />}
      </button>
      <div className="voice-body">
        <div className="voice-wave" onClick={seek} role="slider" aria-valuemin={0} aria-valuemax={Math.round(dur)} aria-valuenow={Math.round(pos)} tabIndex={0}
          onKeyDown={e => { if (!audio.current) return; if (e.key === 'ArrowRight') audio.current.currentTime += 3; if (e.key === 'ArrowLeft') audio.current.currentTime -= 3; }}>
          {bars.map((h, i) => (
            <i key={i} style={{ height: `${Math.round(h * 100)}%` }} className={(i + 0.5) / BARS <= progress ? 'on' : ''} />
          ))}
        </div>
        <div className="voice-meta">
          <span>{fmtDur(playing || pos ? pos : dur)}</span>
          {(playing || pos > 0) && <button type="button" className="voice-speed" onClick={cycleSpeed}>{speed}×</button>}
        </div>
      </div>
      <audio ref={audio} src={src} preload="metadata"
        onLoadedMetadata={e => { if (isFinite(e.currentTarget.duration) && e.currentTarget.duration > 0) setDur(e.currentTarget.duration); }}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setPos(0); }} />
    </div>
  );
}
